import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { DynamicIcon } from './icons/DynamicIcon';
import toast from 'react-hot-toast';

interface FeedbackFormProps {
  onClose: () => void;
  onSubmitSuccess?: () => void;
}

export const FeedbackForm: React.FC<FeedbackFormProps> = ({ 
  onClose,
  onSubmitSuccess
}) => {
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState<number>(5);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Você precisa estar logado para enviar feedback');
      return;
    }

    if (!message.trim()) {
      toast.error('Por favor, digite uma mensagem');
      return;
    }

    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('feedback')
        .insert([
          {
            user_id: user.id,
            message,
            rating
          }
        ]);

      if (error) throw error;
      
      toast.success('Feedback enviado com sucesso! Obrigado pela sua contribuição.');
      onSubmitSuccess?.();
      onClose();
    } catch (err: any) {
      console.error('Error submitting feedback:', err);
      toast.error('Erro ao enviar feedback');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg max-w-md w-full">
      <div className="flex justify-between items-center p-6 border-b">
        <div className="flex items-center">
          <DynamicIcon name="messageSquare" size={24} className="text-blue-600 mr-3" />
          <h2 className="text-xl font-semibold text-gray-900">Enviar Feedback</h2>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 rounded-full"
        >
          <DynamicIcon name="x" size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Avaliação
          </label>
          <div className="flex items-center space-x-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                className="focus:outline-none"
              >
                <DynamicIcon 
                  name="star" 
                  size={24} 
                  className={star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'} 
                />
              </button>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sua Mensagem
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="Compartilhe sua experiência, sugestões ou problemas encontrados..."
            required
          />
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            {loading ? (
              <>
                <DynamicIcon name="loader" size={16} className="animate-spin mr-2" />
                Enviando...
              </>
            ) : (
              <>
                <DynamicIcon name="send" size={16} className="mr-2" />
                Enviar Feedback
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};